/**
 * API Endpoints Configuration
 * Base URL: api.auting.it
 */
import { getConfig, getEnvironment,  isDevelopment as isDevEnv, isStaging as isStageEnv } from "./environment";

const config = getConfig();

// Environment detection (from central environment helpers)
const isDevelopment = isDevEnv();
const isStaging = isStageEnv();

// Base URLs
export const API_BASE_URL_V3 = config.api.baseUrl || 'https://api.auting.it/v3.2';
export const API_BASE_URL = config.api.oldBaseUrl || API_BASE_URL_V3.replace(/\/v3\.2\/?$/, '/api');

// Assets URLs
export const ASSETS_BASE_URL = isStaging
  ? 'https://assets.stage.auting.it'
  : 'https://assets.auting.it';

// API Endpoints
export const API_ENDPOINTS = {
  // Base endpoints
  BASE: API_BASE_URL,
  V3: API_BASE_URL_V3,
  ASSETS: ASSETS_BASE_URL,
  
  SETTINGS: `${API_BASE_URL_V3}/settings`,

  // Authentication endpoints
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    LOGOUT: `${API_BASE_URL}/auth/logout`,
    REFRESH: `${API_BASE_URL}/auth/refresh`,
    VERIFY: `${API_BASE_URL}/auth/verify`
  },

  // Vehicle Research endpoints (from vehicle-research.js)
  VEHICLES: {
    SEARCH: `${API_BASE_URL_V3}/vehicles/search`,
    // Native apiv2-alveare SearchController (TypeORM), not the legacy JHC gateway.
    SUGGESTED: `${API_BASE_URL_V3}/search/suggested`,
    DETAILS: (ownerID, carID) => `${API_BASE_URL_V3}/vehicle/getCarProfile/${ownerID}/${carID}`,
  },

  // Reservation endpoints (from reservation-service.js)
  RESERVATIONS: {
    // V1 endpoints
    CREATE: `${API_BASE_URL_V3}/reservation/create`,
    SET_STATE: (reservationId) => `${API_BASE_URL}/reservation/setStato/${reservationId}`,
    
    VERIFY_AVAILABILITY: `${API_BASE_URL_V3}/reservation/getVerificaDisponibilitaPrezzo`,
    AS_DRIVER: `${API_BASE_URL_V3}/reservations/as-driver`,
    AS_OWNER: `${API_BASE_URL_V3}/reservations/as-owner`,
    DETAILS: (reservationId) => `${API_BASE_URL_V3}/reservations/${reservationId}`,
    CHATS_AS_OWNER: `${API_BASE_URL_V3}/reservations/chats-as-owner`,
    CHATS_AS_DRIVER: `${API_BASE_URL_V3}/reservations/chats-as-driver`,
    
    // Chat endpoints
    GET_CHAT: (reservationId) => `${API_BASE_URL}/reservation/events/getChat/${reservationId}`,
    SEND_MESSAGE: (reservationId) => `${API_BASE_URL}/reservation/events/createChat/${reservationId}`,
    
    // Rating endpoints
    CREATE_RATING: (reservationId) => `${API_BASE_URL}/reservation/rating/create/${reservationId}`
  },

  // Trip/Vehicle Control endpoints
  TRIPS: {
    STATE: (reservationId) => `${API_BASE_URL}/trips/${reservationId}/state`,
    CHECKIN: (reservationId) => `${API_BASE_URL}/trips/${reservationId}/checkin`,
    CHECKOUT: (reservationId) => `${API_BASE_URL}/trips/${reservationId}/checkout`,
    START: (reservationId) => `${API_BASE_URL}/trips/${reservationId}/start`,
    STOP: (reservationId) => `${API_BASE_URL}/trips/${reservationId}/stop`
  },

  // Vehicle Control endpoints
  VEHICLE: {
    STATE: (vehicleId) => `${API_BASE_URL}/vehicle/${vehicleId}/state`,
    UNLOCK: (vehicleId) => `${API_BASE_URL}/vehicle/${vehicleId}/unlock`,
    LOCK: (vehicleId) => `${API_BASE_URL}/vehicle/${vehicleId}/lock`
  },

  // Upload endpoints
  UPLOAD: {
    CHECK_IN_PICTURE: (reservationId, type) => `${API_BASE_URL_V3}/upload/${reservationId}/check-in/${type}.jpg`,
    CHECK_OUT_PICTURE: (reservationId, type) => `${API_BASE_URL_V3}/upload/${reservationId}/check-out/${type}.jpg`
  },

  // User endpoints
  USER: {
    //PROFILE: `${API_BASE_URL_V3}/user/profile`,
    //UPDATE_PROFILE: `${API_BASE_URL}/user/profile/update`,
    //DOCUMENTS: `${API_BASE_URL}/user/documents`,
    //VEHICLES: `${API_BASE_URL}/user/vehicles`,
    INFO: (userId) => `${API_BASE_URL_V3}/users/info/${userId}`,
    // Registrazione token push FCM dell'utente loggato (richiede Authorization: Bearer)
    NOTIFICATION_TOKEN: `${API_BASE_URL_V3}/users/me/notification-token`
  },

  // Asset URLs
  ASSETS: {
    VEHICLE_IMAGE: (imagePath) => `${ASSETS_BASE_URL}/${imagePath}`,
    USER_AVATAR: (imagePath) => `${ASSETS_BASE_URL}/${imagePath}`,
    DOCUMENT: (documentPath) => `${ASSETS_BASE_URL}/${documentPath}`
  }
};

// HTTP Headers
export const HTTP_HEADERS = {
  JSON: {
    'Content-Type': 'application/json; charset=UTF-8'
  },
  
  MULTIPART: {
    'Content-Type': 'multipart/form-data'
  },
  
  IMAGE: {
    'Content-Type': 'image/jpeg',
    'Accept': 'image/jpeg',
    'Connection': 'keep-alive',
    'User-Agent': 'AutingAppV3'
  },

  // JHC Token for vehicle search (from vehicle-research.js)
  JHC_AUTH: {
    'Authorization': 'JHC-TOKEN username="jhc", sessionid="ahtS1!6&009qEjss"'
  }
};

// Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
};

// Reservation States (from the original Flutter code)
export const RESERVATION_STATES = {
  PENDING: 1,
  ACCEPTED: 2,
  IN_PROGRESS: 3,
  REFUSED: 4,
  CANCELLED: 5,
  EXPIRED: 6,
  COMPLETED: 7
};

// Vehicle States
export const VEHICLE_STATES = {
  AVAILABLE: 'available',
  BUSY: 'busy',
  MAINTENANCE: 'maintenance',
  OFFLINE: 'offline'
};

// Check-in/Check-out Picture Types
export const PICTURE_TYPES = {
  FRONT: 'front',
  BACK: 'back',
  LEFT: 'left',
  RIGHT: 'right',
  INTERIOR: 'interior',
  DASHBOARD: 'dashboard',
  FUEL: 'fuel',
  DAMAGE: 'damage'
};

// Error Codes (from reservation service)
export const ERROR_CODES = {
  MISSING_USER_INFO: 800,
  MISSING_MESSAGE: 128,
  AUTH_ERROR: 401,
  VALIDATION_ERROR: 400,
  NOT_FOUND: 404,
  RATE_LIMIT: 429,
  SERVER_ERROR: 500
};

// Utility functions
export const UTILS = {
  /**
   * Build URL parameter string
   */
  getParam: (key, value) => {
    return value != null ? `${key}=${encodeURIComponent(value)}&` : '';
  },

  /**
   * Check if response status is OK
   */
  isResponseOk: (statusCode) => {
    return statusCode >= 200 && statusCode < 300;
  },

  /**
   * Format date for API (YYYYMMDDHHMM format)
   */
  formatDateForAPI: (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}`;
  },

  /**
   * Get full asset URL
   */
  getAssetUrl: (path) => {
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return `${ASSETS_BASE_URL}/${path}`;
  }
};

// Environment info
export const ENV_INFO = {
  isDevelopment,
  isStaging,
  isProduction: !isDevelopment && !isStaging,
  apiBaseUrl: API_BASE_URL,
  assetsBaseUrl: ASSETS_BASE_URL
};

// Export default configuration
export default {
  API_ENDPOINTS,
  HTTP_HEADERS,
  HTTP_STATUS,
  RESERVATION_STATES,
  VEHICLE_STATES,
  PICTURE_TYPES,
  ERROR_CODES,
  UTILS,
  ENV_INFO
};